import { pickBy } from 'ramda';
import { useCallback, useEffect, useRef } from 'react';
import { Alert, Platform } from 'react-native';
import { useCurrentUserProfile } from '../../../../functions/auth/utils';
import useBehaviorSubject from '../../../../functions/hooks/useBehaviorSubject';
import { usePersistFn } from '../../../../functions/hooks/usePersistFn';
import { useServiceFactory } from '../../../../functions/services';
import AsyncStorage from '../../../../utils/asyncStorage';
import { makeDebug } from '../../../../utils/makeDebug';
import { isChatDebugEnabled } from '../utils';

const debug = makeDebug('chat:settings', isChatDebugEnabled('settings'));

export type ChatSettings = {
  isMutedInLive: boolean;
  isMuted: boolean;
  isCanSendMessageToMe: boolean;
  isAutoAnnouncement: boolean;
};

type ChatSettingItemGroup = {
  name: string;
  items: ChatSettingItem[];
};
type ChatSettingItem = {
  property: keyof ChatSettings;
  title: string;
  subTitle: string;
  defaultValue: boolean;
};

export function useChatSettingItems(isSeller: boolean): ChatSettingItemGroup[] {
  return [
    {
      name: 'Mute Option',
      items: [
        {
          property: 'isMutedInLive',
          defaultValue: true,
          title: 'Mute when in live show',
          subTitle: `Don't send me notifications while I'm ${
            isSeller ? 'hosting or watching' : 'watching'
          } a live show`,
        },
        {
          property: 'isMuted',
          defaultValue: false,
          title: 'Mute All',
          subTitle: "Don't send me any notifications at any time",
        },
      ],
    },
    {
      name: 'ALERT ME WHEN THIS EVENTS TAKE PLACE:',
      items: [
        {
          property: 'isCanSendMessageToMe',
          defaultValue: true,
          title: 'Messages',
          subTitle:
            "Don't send me notifications when someone sends message to me",
        },
        {
          property: 'isAutoAnnouncement',
          defaultValue: isSeller ? false : true,
          title: 'Auto Announcement',
          subTitle: isSeller
            ? 'Automatically send a message to my customer who places an order'
            : 'Receive a message automatically anytime when I make a purchase',
        },
      ],
    },
  ];
}

export const useChatSettings = () => {
  const profile = useCurrentUserProfile();
  const uid = profile?.id;
  const hasProfile = profile != null;
  const key = `${profile?.id ?? ''}.chat-settings`;

  const { defaultService } = useServiceFactory();

  const subject$ = AsyncStorage.behaviorSubject$<ChatSettings>(key);
  const [settings, setSettings] = useBehaviorSubject(subject$);

  const updateSettings = usePersistFn(async (params: Partial<ChatSettings>) => {
    params = pickBy((_, key) => params[key] != null, params);
    debug.info('update settings with params: %o', params);
    const resp = await defaultService.chatControllerSetNotification({
      notificationSettingParam: params,
    });
    setSettings(resp);
  });

  const updateSettingsProperty = usePersistFn(
    async <T extends keyof ChatSettings>(key: T, value: boolean) => {
      const existingValue = settings?.[key];
      try {
        if (settings != null) {
          setSettings({
            ...settings,
            [key]: value,
          });
        }
        await updateSettings({
          [key]: value,
        });
      } catch (e) {
        debug.error('an error occurred while update settings: %O', e);
        const msg =
          (e as Error).message ??
          (e as any).error ??
          'An error occurred while update settings';
        const revert = () => {
          if (existingValue != null && settings != null) {
            setSettings({
              ...settings,
              [key]: existingValue,
            });
          }
        };
        if (Platform.OS === 'web') {
          alert(msg);
          revert();
        } else {
          Alert.alert('Error', msg, [
            {
              text: 'OK',
              onPress: revert,
            },
          ]);
        }
      }
    },
  );

  type PropertyUpdater<T extends keyof ChatSettings> = (
    value: ChatSettings[T],
  ) => Promise<void>;

  const propertyUpdaters = useRef(
    new Map<keyof ChatSettings, (value: any) => Promise<void>>(),
  ).current;

  const updateSettingsPropertyFactory = useCallback(
    <T extends keyof ChatSettings>(key: T): PropertyUpdater<T> => {
      let propertyUpdater = propertyUpdaters.get(key) as
        | PropertyUpdater<T>
        | undefined;
      if (propertyUpdater == null) {
        propertyUpdater = updateSettingsProperty.bind(null, key);
        propertyUpdaters.set(key, propertyUpdater);
      }
      return propertyUpdater;
    },
    [propertyUpdaters, updateSettingsProperty],
  );

  useEffect(() => {
    if (!hasProfile || uid == null) {
      return;
    }
    debug('fetching chat notification settings... ');
    void defaultService.chatControllerNotificationSettings().then(res => {
      debug('server returned chat notification settings: %O', res);
      setSettings(res);
    });
  }, [defaultService, setSettings, hasProfile, uid]);

  debug('current chat notification settings: %O', settings);

  return {
    settings,
    updateSettings,
    updateSettingsProperty,
    updateSettingsPropertyFactory,
  };
};
